import configDetails from '../Config/Config';
import DOMPurify from 'dompurify';

/**
 * Image utility functions for handling image uploads and processing
 */

/**
 * Batch convert multiple S3 URIs to presigned URLs using POST API
 * @param {Array} imageSegments - Array of segments with content_type: 'image'
 * @param {string} authIdToken - Authorization token for API call
 * @returns {Promise<Object>} Map of div_id to presigned_url
 */
export const batchConvertS3UrisToPresignedUrls = async (imageSegments, authIdToken) => {
  if (!imageSegments || imageSegments.length === 0) return {};
  
  try {
    const { trackingUrl, GET_S3_FILE_URL } = configDetails;
    
    // Prepare request payload with array of S3 URIs
    const cleanS3Uri = (raw) => {
      if (!raw) return '';
      let cleaned = raw.trim();
      cleaned = cleaned.replace(/[.,;:!?'")\]]+$/, '');
      return cleaned;
    };
    
    const requestPayload = {
      s3_uris: imageSegments.map(segment => ({
        div_id: segment.div_id,
        s3_uri: cleanS3Uri(segment.full_message)
      }))
    };
    
    
    const response = await fetch(`${trackingUrl}${GET_S3_FILE_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authIdToken
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      throw new Error(`Failed to batch convert S3 URIs: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Create a mapping of div_id to presigned_url
    const presignedUrlMap = {};
    if (Array.isArray(data)) {
      data.forEach(item => {
        if (item.div_id && item.presigned_url) {
          presignedUrlMap[item.div_id] = item.presigned_url;
        }
      });
    }
    
    return presignedUrlMap;
  } catch (error) {
    console.error('❌ Failed to batch convert S3 URIs:', error);
    return {};
  }
};

/**
 * Create image segment for transcript display
 * @param {string} download_url - The image download URL
 * @param {string} imageDivId - The unique div ID for the image
 * @returns {Object} Image segment object
 */
export const createImageSegment = (download_url, imageDivId, calculateElapsedTime) => {
  const getFormattedTimestamp = (dateObj) => dateObj.toISOString().slice(0, 19);
  
  return {
    text: `<img src="${download_url}" alt="Uploaded image" style="max-width: 300px; height: auto; border-radius: 8px; margin: 10px 0;" />`,
    isPartial: false,
    timestamp: new Date().toISOString(),
    div_id: imageDivId,
    sequence_id: 1,
    create_timestamp: getFormattedTimestamp(new Date()),
    type: 'image',
    final: true,
    timerAtCreation: typeof calculateElapsedTime === 'function' ? calculateElapsedTime() : 0,
  };
};

/**
 * Send image message to viewers via WebSocket
 * @param {Object} socketRef - WebSocket reference
 * @param {string} effectiveConversationId - Conversation ID
 * @param {string} download_url - Image download URL
 * @param {string} imageDivId - Unique div ID for the image
 * @param {Object} isPausedRef - Paused state reference
 */
export const sendImageToViewers = (socketRef, effectiveConversationId, download_url, imageDivId, isPausedRef, start_time = null) => {
  if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
    const imageMessage = {
      action: "segment",
      conversation_id: effectiveConversationId,
      text: `<img src="${download_url}" alt="Uploaded image" style="max-width: 300px; height: auto; border-radius: 8px; margin: 10px 0;" />`,
      is_partial: false,
      is_pause: isPausedRef.current,
      action_type: "manual_text",
      source: "manual",
      div_id: imageDivId,
      start_time: start_time
    };
    socketRef.current.send(JSON.stringify(imageMessage));
  }
};

/**
 * Get effective conversation ID from various sources
 * @param {string} conversationId - Primary conversation ID
 * @param {string} conversationIdFromUrl - Conversation ID from URL
 * @param {Function} getCookie - Function to get cookie value
 * @returns {string|null} Effective conversation ID
 */
export const getEffectiveConversationId = (conversationId, conversationIdFromUrl, getCookie) => {
  let effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
  
  if (!effectiveConversationId || effectiveConversationId === 'null') {
    // Fallback: try to extract from URL again
    const pathSegments = window.location.pathname.split('/');
    const urlConvId = pathSegments[pathSegments.length - 1];
    if (urlConvId && urlConvId !== 'record' && urlConvId.length > 10) {
      effectiveConversationId = urlConvId;
    }
  }
  
  return effectiveConversationId;
};

/**
 * Update state tracking for image segments
 * @param {string} imageDivId - Image div ID
 * @param {Function} setCurrentDivId - Set current div ID function
 * @param {Object} currentDivIdRef - Current div ID reference
 * @param {Object} sequenceMapRef - Sequence map reference
 * @param {Function} setSequenceMap - Set sequence map function
 */
export const updateImageStateTracking = (imageDivId, setCurrentDivId, currentDivIdRef, sequenceMapRef, setSequenceMap) => {
  setCurrentDivId(imageDivId);
  currentDivIdRef.current = imageDivId;
  
  // Reset sequence tracking for this div_id and prepare for next div_id
  sequenceMapRef.current[imageDivId] = 1;
  setSequenceMap(prev => ({ ...prev, [imageDivId]: 1 }));
};

/**
 * Main handler for processing image uploads
 * - Creates image segment for transcript display
 * - Sends image to viewers via WebSocket
 * - Updates state tracking
 * Note: Does NOT store image data in database via INSERT_MESSAGE API
 * @param {Object} imageData - Image data containing download_url and s3_uri
 * @param {Object} context - Context object containing all necessary functions and state
 * @returns {Promise<void>}
 */
export const handleImageUpload = async (imageData, context) => {
  const {
    conversationId,
    conversationIdFromUrl,
    getCookie,
    getNewDivId,
    setCurrentDivId,
    currentDivIdRef,
    sequenceMapRef,
    setSequenceMap,
    setTranscriptSegments,
    socketRef,
    isPausedRef,
    getAuthenticatedUserId,
    calculateElapsedTime,
    sessionId
  } = context;

  const { download_url, s3_uri } = imageData;

  // Get effective conversation ID
  const effectiveConversationId = getEffectiveConversationId(conversationId, conversationIdFromUrl, getCookie);
  
  if (!effectiveConversationId || effectiveConversationId === 'null') {
    console.warn('❌ [handleImageUpload] No valid conversation ID found');
    return;
  }

  // Generate new div_id for image
  const imageDivId = getNewDivId();

  // Create image segment for transcript
  const imageSegment = createImageSegment(download_url, imageDivId, calculateElapsedTime);

  // Update state tracking
  updateImageStateTracking(imageDivId, setCurrentDivId, currentDivIdRef, sequenceMapRef, setSequenceMap);

  // Add image to transcript segments
  setTranscriptSegments(prev => {
    const newSegments = [...prev, imageSegment];
    return newSegments;
  });

  // Send image to viewers via WebSocket
  sendImageToViewers(socketRef, effectiveConversationId, download_url, imageDivId, isPausedRef, typeof calculateElapsedTime === 'function' ? calculateElapsedTime() : null);

  
};
